import React from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';

/**
 * @component FunnelChart
 * @description Custom funnel visualization showing Sent → Delivered → Opened → Clicked progression
 * Uses BarChart styled to create funnel appearance
 * @param {Object} props
 * @param {number} props.sent - Number of emails sent
 * @param {number} props.delivered - Number of emails delivered
 * @param {number} props.opened - Number of emails opened
 * @param {number} props.clicked - Number of emails clicked
 * @param {boolean} [props.loading=false] - Loading state
 * @param {number} [props.height=200] - Chart height
 * @returns {JSX.Element}
 */
export function FunnelChart({ 
  sent = 0, 
  delivered = 0, 
  opened = 0, 
  clicked = 0, 
  loading = false,
  height = 200 
}) {
  const theme = useTheme();

  if (loading || sent === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minHeight: height }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const stages = [
    { label: 'Sent', value: sent, percentage: 100 },
    { label: 'Delivered', value: delivered, percentage: sent > 0 ? (delivered / sent * 100) : 0 },
    { label: 'Opened', value: opened, percentage: sent > 0 ? (opened / sent * 100) : 0 },
    { label: 'Clicked', value: clicked, percentage: sent > 0 ? (clicked / sent * 100) : 0 },
  ];

  return (
    <Box>
      <BarChart
        layout="horizontal"
        margin={{ left: 80 }}
        yAxis={[
          {
            data: stages.map(s => s.label),
            scaleType: 'band',
          },
        ]}
        xAxis={[
          {
            label: 'Count',
          },
        ]}
        series={[
          {
            data: stages.map(s => s.value),
            color: theme.palette.primary.main,
          },
        ]}
        height={height}
        slotProps={{
          axisContent: {
            sx: {
              '& .MuiChartsAxis-label': {
                overflow: 'visible',
                textOverflow: 'clip',
              },
            },
          },
        }}
        sx={{
          '& .MuiChartsAxis-root': {
            fontSize: '0.75rem',
          },
          '& .MuiChartsBarElement-root rect': {
            rx: '4px',
            ry: '4px',
          },
        }}
      />
      {/* Additional labels with percentages */}
      <Stack spacing={1} sx={{ mt: 2 }}>
        {stages.map((stage, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium">
              {stage.label}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {stage.value.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({stage.percentage.toFixed(1)}%)
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

